import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container, Paper, Typography, Box, Button, CircularProgress,
  Alert, RadioGroup, FormControlLabel, Radio, IconButton,
  Breadcrumbs, Link
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel } from '@mui/icons-material';
import api from '../config/api';
import { isFlightBookingEnabled } from '../config/features';
import FlightSummaryCard from '../components/FlightSummaryCard';
import { openGoogleFlightsInNewTab } from '../utils/googleFlightsUrl';

function FlightFareSelector() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const flightBookingEnabled = isFlightBookingEnabled();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get offer ID from URL (single bundled offer with 2 slices)
  const offerId = searchParams.get('offerId');
  const passengers = searchParams.get('passengers') || '1';
  const origin = searchParams.get('origin');
  const destination = searchParams.get('destination');
  const departDate = searchParams.get('departDate');
  const returnDate = searchParams.get('returnDate');
  const alternativeOfferIds = searchParams.get('alternativeOfferIds'); // Comma-separated list of alternative cabin class offer IDs
  
  // Flight and fare data (single offer with multiple fare options)
  const [offer, setOffer] = useState(null);
  const [fareOptions, setFareOptions] = useState([]);
  const [selectedFare, setSelectedFare] = useState(null);

  useEffect(() => {
    const fetchFares = async () => {
      try {
        setLoading(true);
        
        // Fetch the bundled offer (contains 2 slices)
        const offerRes = await api.get(`/duffel/offers/${offerId}`);
        const bundledOffer = offerRes.data.offer;
        
        console.log('✈️ Bundled offer:', bundledOffer);
        console.log('✈️ Offer conditions:', bundledOffer.conditions);
        console.log('✈️ Slices:', bundledOffer.slices);
        
        setOffer(bundledOffer);
        setSelectedFare(bundledOffer); // Default to the current offer
        
        // Check if we have alternative offer IDs passed from search results
        if (alternativeOfferIds) {
          // Fetch alternative offers directly (no additional searches needed!)
          const altIds = alternativeOfferIds.split(',').filter(Boolean);
          console.log(`🎫 Fetching ${altIds.length} alternative cabin class offers from search results`);
          
          try {
            const altPromises = altIds.map(id => 
              api.get(`/duffel/offers/${id}`).catch(err => {
                console.warn(`⚠️ Offer ${id} expired or unavailable:`, err.response?.status);
                return null;
              })
            );
            const altResponses = await Promise.allSettled(altPromises);
            
            const altOffers = altResponses
              .filter(r => r.status === 'fulfilled' && r.value?.data?.offer)
              .map(r => r.value.data.offer);
            
            const expiredCount = altIds.length - altOffers.length;
            console.log(`✅ Fetched ${altOffers.length} alternative offers${expiredCount > 0 ? ` (${expiredCount} expired)` : ''}`);
            
            // Combine with base offer
            const allFares = [bundledOffer, ...altOffers];
            
            // Sort by price and enrich
            const enrichedFares = allFares
              .sort((a, b) => parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0))
              .map(fare => {
                const outboundFare = fare.slices?.[0]?.fare_brand_name || 'Economy';
                const inboundFare = fare.slices?.[1]?.fare_brand_name || 'Economy';
                const sameFareBrand = outboundFare === inboundFare;
                
                return {
                  ...fare,
                  fare_brand_name: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
                  fare_class: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
                  outbound_fare_brand: outboundFare,
                  inbound_fare_brand: inboundFare
                };
              });
            
            setFareOptions(enrichedFares);
            console.log(`✅ Displaying ${enrichedFares.length} fare options (no additional searches!)`);
            
          } catch (altError) {
            console.warn('⚠️ Could not fetch some alternative offers:', altError);
            // Fallback to just showing the selected offer
            const outboundFare = bundledOffer.slices?.[0]?.fare_brand_name || 'Economy';
            const inboundFare = bundledOffer.slices?.[1]?.fare_brand_name || 'Economy';
            const sameFareBrand = outboundFare === inboundFare;
            
            setFareOptions([{
              ...bundledOffer,
              fare_brand_name: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
              fare_class: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
              outbound_fare_brand: outboundFare,
              inbound_fare_brand: inboundFare
            }]);
          }
        } else {
          // No alternatives passed from search - search for other cabin classes as fallback
          console.log('ℹ️ No alternative fare options provided from search results');
          console.log('🔍 Searching for other cabin classes as fallback...');
          
          try {
            const fareParams = new URLSearchParams({
              origin,
              destination,
              departure_date: departDate,
              return_date: returnDate,
              passengers
            });
            
            const fareRes = await api.get(`/duffel/offers/${offerId}/fare-alternatives?${fareParams.toString()}`);
            const alternatives = fareRes.data.alternatives || [];
            
            console.log(`🎫 Found ${alternatives.length} fare alternatives via search`);
            
            if (alternatives.length > 0) {
              // Combine base offer + alternatives
              const allFares = [bundledOffer, ...alternatives.map(alt => alt.offer || alt)];
              
              const enrichedFares = allFares
                .sort((a, b) => parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0))
                .map(fare => {
                  const outboundFare = fare.slices?.[0]?.fare_brand_name || 'Economy';
                  const inboundFare = fare.slices?.[1]?.fare_brand_name || 'Economy';
                  const sameFareBrand = outboundFare === inboundFare;
                  
                  return {
                    ...fare,
                    fare_brand_name: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
                    fare_class: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
                    outbound_fare_brand: outboundFare,
                    inbound_fare_brand: inboundFare
                  };
                });
              
              setFareOptions(enrichedFares);
              console.log(`✅ Displaying ${enrichedFares.length} fare options`);
            } else {
              // Still no alternatives - show only the selected offer
              const outboundFare = bundledOffer.slices?.[0]?.fare_brand_name || 'Economy';
              const inboundFare = bundledOffer.slices?.[1]?.fare_brand_name || 'Economy';
              const sameFareBrand = outboundFare === inboundFare;
              
              setFareOptions([{
                ...bundledOffer,
                fare_brand_name: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
                fare_class: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
                outbound_fare_brand: outboundFare,
                inbound_fare_brand: inboundFare
              }]);
            }
          } catch (fareError) {
            console.warn('⚠️ Could not search for fare alternatives:', fareError);
            
            // Fallback: just show the selected offer
            const outboundFare = bundledOffer.slices?.[0]?.fare_brand_name || 'Economy';
            const inboundFare = bundledOffer.slices?.[1]?.fare_brand_name || 'Economy';
            const sameFareBrand = outboundFare === inboundFare;
            
            setFareOptions([{
              ...bundledOffer,
              fare_brand_name: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
              fare_class: sameFareBrand ? outboundFare : `${outboundFare} / ${inboundFare}`,
              outbound_fare_brand: outboundFare,
              inbound_fare_brand: inboundFare
            }]);
          }
        }
        
      } catch (e) {
        console.error('❌ Error fetching fares:', e);
        const errorMsg = e.response?.data?.error || e.message;
        
        if (errorMsg.includes('internal_error') || errorMsg.includes('airline')) {
          setError('⚠️ This flight is temporarily unavailable from the airline. Please go back and try another flight option.');
        } else if (errorMsg.includes('no longer available') || errorMsg.includes('expired')) {
          setError('⚠️ This flight offer has expired. Please go back and search again for current options.');
        } else {
          setError(errorMsg || 'Failed to load fare options. Please try another flight.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (offerId) {
      fetchFares();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, origin, destination, departDate, returnDate, passengers]);

  const handleContinueToCheckout = () => {
    if (!selectedFare) {
      setError('Please select a fare option');
      return;
    }

    if (!flightBookingEnabled) {
      const opened = openGoogleFlightsInNewTab({
        origin,
        destination,
        departDate,
        returnDate,
        passengers,
      });

      if (!opened) {
        setError(t('flightBooking.missingSearchParams'));
      }
      return;
    }

    // Navigate to checkout page with selected bundled offer ID
    const params = new URLSearchParams({
      passengers,
      origin,
      destination,
      departDate,
      returnDate,
      tripType: 'round_trip'
    });
    
    navigate(`/duffel/offer/${selectedFare.id}?${params.toString()}`);
  };

  const handleBack = () => {
    const params = new URLSearchParams({
      passengers,
      origin,
      destination,
      departDate,
      returnDate,
      tripType: 'round_trip'
    });
    navigate(`/flights/search?${params.toString()}`);
  };

  const getFareType = (offer) => {
    // Use fare_brand_name from Duffel if available
    if (offer?.fare_brand_name) {
      return offer.fare_brand_name;
    }
    
    // Also check fare_class (our custom field)
    if (offer?.fare_class) {
      return offer.fare_class;
    }
    
    // Fallback: extract from slices
    if (offer?.slices?.[0]?.fare_brand_name) {
      return offer.slices[0].fare_brand_name;
    }
    
    // Last resort: compute from conditions
    const cabinClass = offer?.cabin_class || 'economy';
    const conditions = offer?.conditions;
    const isRefundable = conditions?.refund_before_departure?.allowed;
    const isChangeable = conditions?.change_before_departure?.allowed;
    
    const cabinNames = {
      'first': 'First Class',
      'business': 'Business',
      'premium_economy': 'Premium Economy',
      'economy': 'Economy'
    };
    
    const baseName = cabinNames[cabinClass] || 'Economy';
    
    if (isRefundable && isChangeable) return `Flexible ${baseName}`;
    if (isChangeable) return `Standard ${baseName}`;
    return `Basic ${baseName}`;
  };

  const formatPrice = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Loading fare options...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          ← Go Back
        </Button>
      </Container>
    );
  }

  // Check if we have valid offer data for round trip
  const hasValidRoundTrip = offer && (
    (offer.slices && offer.slices.length >= 2) || // Duffel raw format
    (offer.return_flight) // Formatted format
  );

  if (!offer || !hasValidRoundTrip) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 8 }}>
        <Alert severity="warning">Invalid flight offer. Please go back and search again.</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          ← Go Back
        </Button>
      </Container>
    );
  }

  const MARKUP = 0.05;
  
  // Prepare flight data for the summary card
  // Backend returns raw Duffel offers with slices array
  const outboundFlightData = offer.slices && offer.slices[0] ? {
    ...offer.slices[0],
    passengers: offer.passengers,
    baggage_allowances: offer.passengers?.[0]?.baggage_allowances || []
  } : null;
  
  const inboundFlightData = offer.slices && offer.slices[1] ? {
    ...offer.slices[1],
    passengers: offer.passengers,
    baggage_allowances: offer.passengers?.[0]?.baggage_allowances || []
  } : null;

  return (
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs separator="›" aria-label="flight selection breadcrumb">
          <Typography variant="body2" color="text.secondary">
            Orders
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/deals?tab=flights')}
            sx={{ 
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' },
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
          >
            New order
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(-1)}
            sx={{ 
              textDecoration: 'none',
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {origin} to {destination}
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(-1)}
            sx={{ 
              textDecoration: 'none',
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {destination} to {origin}
          </Link>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Fare options
          </Typography>
        </Breadcrumbs>
      </Box>

      <Paper sx={{ p: 4 }}>
        {/* Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Select Your Fare
          </Typography>
        </Box>

        {/* Flight Summary Card */}
        <FlightSummaryCard
          outboundFlight={outboundFlightData}
          inboundFlight={inboundFlightData}
        />

        {/* Fare Options */}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
          Available Fares
        </Typography>

        <RadioGroup
          value={selectedFare?.id}
          onChange={(e) => {
            const fare = fareOptions.find(f => f.id === e.target.value);
            setSelectedFare(fare);
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fareOptions.map((fare) => {
              const isSelected = selectedFare?.id === fare.id;
              const conditions = fare.conditions;
              const isRefundable = conditions?.refund_before_departure?.allowed;
              const isChangeable = conditions?.change_before_departure?.allowed;
              const farePrice = parseFloat(fare.total_amount);
              const withMarkup = farePrice * (1 + MARKUP);

              return (
                <Paper
                  key={fare.id}
                  sx={{
                    p: 3,
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'rgba(25, 118, 210, 0.04)' : 'white',
                    cursor: 'pointer',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(25, 118, 210, 0.04)' : '#f5f5f5',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => setSelectedFare(fare)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <FormControlLabel
                      value={fare.id}
                      control={<Radio />}
                      label=""
                      sx={{ m: 0, mt: 0.5 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                            {fare.cabin_class?.replace('_', ' ')?.toUpperCase() || 'ECONOMY'}
                          </Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {getFareType(fare)}
                          </Typography>
                          {/* Show individual slice fare brands if different */}
                          {fare.outbound_fare_brand && fare.inbound_fare_brand && fare.outbound_fare_brand !== fare.inbound_fare_brand && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Outbound: {fare.outbound_fare_brand} • Inbound: {fare.inbound_fare_brand}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            total amount from
                          </Typography>
                          <Typography variant="h5" fontWeight={700}>
                            {formatPrice(withMarkup, fare.total_currency)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Features List */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Changeable */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isChangeable ? (
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
                          )}
                          <Typography variant="body2">
                            {isChangeable 
                              ? `Changeable ${conditions?.change_before_departure?.penalty_amount 
                                  ? `(${formatPrice(parseFloat(conditions.change_before_departure.penalty_amount), conditions.change_before_departure.penalty_currency)} fee)` 
                                  : ''}` 
                              : 'Not changeable'}
                          </Typography>
                        </Box>

                        {/* Refundable */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isRefundable ? (
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
                          )}
                          <Typography variant="body2">
                            {isRefundable 
                              ? `Refundable ${conditions?.refund_before_departure?.penalty_amount 
                                  ? `(${formatPrice(parseFloat(conditions.refund_before_departure.penalty_amount), conditions.refund_before_departure.penalty_currency)} fee)` 
                                  : ''}` 
                              : 'Not refundable'}
                          </Typography>
                        </Box>

                        {/* Price & Space Hold */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Hold <u>price</u> & <u>space</u>
                          </Typography>
                        </Box>

                        {/* Baggage */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Includes carry-on bags
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Includes checked bags
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </RadioGroup>

        {/* Summary and Continue Button */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'grey.300' }}>
          {!flightBookingEnabled && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('flightBooking.externalBookingNotice')}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total for {passengers} passenger{passengers !== '1' ? 's' : ''}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {selectedFare ? formatPrice(parseFloat(selectedFare.total_amount) * (1 + MARKUP), selectedFare.total_currency) : '$0.00'}
              </Typography>
              {!flightBookingEnabled && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {t('flightBooking.externalBookingHelp')}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinueToCheckout}
              disabled={!selectedFare}
              sx={{
                bgcolor: 'black',
                color: 'white',
                px: 6,
                '&:hover': {
                  bgcolor: '#333'
                }
              }}
            >
              {flightBookingEnabled
                ? t('flightBooking.continueToCheckout')
                : t('flightBooking.continueOnGoogleFlights')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default FlightFareSelector;
